import { useDebouncedCallback } from '@react-hookz/web/useDebouncedCallback/index.js';
import type { UseComboboxInputValueChange } from 'downshift';
import { useCombobox } from 'downshift';
import { useRef, useState } from 'react';
import { Popover, type PopoverState } from 'react-tiny-popover';

import type { WeatherLocation } from '../api/useLocationAPI';
import { useLocationAPI } from '../api/useLocationAPI';

import { LocationInput } from './LocationInput';
import styles from './LocationPicker.module.scss';

type Props = {
	initialLocation: WeatherLocation | null;
	onLocationChange?: (location: WeatherLocation | null) => void;
	className?: string;
};

export function LocationPicker({ initialLocation, onLocationChange, className }: Props) {
	const [value, setValue] = useState<string | null>(initialLocation?.city ?? null);
	const [shouldFetch, setShouldFetch] = useState(false);
	const [searchValue, setSearchValue] = useState(initialLocation?.city ?? null);
	const inputRef = useRef<HTMLInputElement | null>(null);

	const onInputValueChangeHandler = useDebouncedCallback(
		(e: UseComboboxInputValueChange<WeatherLocation>) => {
			setSearchValue(e.inputValue);
		},
		[setSearchValue],
		200
	);
	const { locationSuggestions, isLoading } = useLocationAPI({ searchTerm: searchValue, shouldFetch });

	const { isOpen, getLabelProps, getMenuProps, highlightedIndex, getItemProps, getInputProps } = useCombobox<WeatherLocation>({
		items: locationSuggestions,
		inputValue: value ?? undefined,
		onInputValueChange(event) {
			onInputValueChangeHandler(event);
			setValue(event.inputValue);
		},
		itemToString(item) {
			return item?.city ?? '';
		},
		onSelectedItemChange(changes) {
			onLocationChange?.(changes.selectedItem);
			setValue(changes.selectedItem?.city ?? null);
			inputRef.current?.blur();
		},
	});

	// eslint-disable-next-line react-hooks/refs
	const inputProps = getInputProps({
		onFocus: () => setShouldFetch(true),
		onBlur: () => setShouldFetch(false),
		onClick(event) {
			if (isOpen) {
				// prevent closing of dropdown on input click if dropdown is already open
				(event.nativeEvent as any).preventDownshiftDefault = true;
			}
		},
		ref: inputRef,
	});

	return (
		<div className={`heading1 ${styles.wrapper} ${className ?? ''}`}>
			<label className={styles.label} {...getLabelProps()}>
				Ort:
			</label>
			<Popover
				isOpen={true}
				positions={['bottom', 'top']}
				align="start"
				ref={inputProps.ref}
				containerClassName={styles.popoverContainer}
				content={({ childRect }: PopoverState) => {
					return (
						<ul
							{...getMenuProps({})}
							className={styles.dropdown}
							style={{
								width: childRect.width,
							}}
						>
							{isOpen &&
								!isLoading &&
								locationSuggestions.map((item, index) => (
									<li
										className={`${styles.item} ${highlightedIndex === index ? styles.highlight : ''}`}
										key={item.value}
										{...getItemProps({
											item,
											index,
										})}
									>
										{item.label}
									</li>
								))}
						</ul>
					);
				}}
			>
				<LocationInput {...inputProps} />
			</Popover>
		</div>
	);
}
